import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import PlayerStatsTable from './PlayerStatsTable';

const RosterDisplay = ({ league_id }) => {
  const timeframeOptions = [
    { value: "SEASON", label: "Season" },
    { value: "LAST_30", label: "Last 30" },
    { value: "LAST_15", label: "Last 15" },
    { value: "LAST_7", label: "Last 7" },
  ];

  const [timeframe, setTimeframe] = useState(timeframeOptions[0]);
  const [rosters, setRosters] = useState({});
  const [teams, setTeams] = useState({});
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamOptions, setTeamOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [showAutofillInstructions, setShowAutofillInstructions] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');

  useEffect(() => {
    fetchTeams();
  }, [league_id]);

  const fetchTeams = async () => {
    try {
      const response = await fetch(`/get_teams/${league_id}`);
      if (!response.ok) throw new Error('Failed to fetch teams');
      const data = await response.json();
      setTeams(data);

      // Build team selector options
      const teamOpts = Object.entries(data).map(([teamId, teamName]) => ({
        value: teamId,
        label: teamName
      }));
      setTeamOptions(teamOpts);

      // Default to first team and fetch its roster
      if (teamOpts.length > 0) {
        setSelectedTeam(teamOpts[0]);
        fetchTeamRoster(teamOpts[0].value, timeframe.value, data);
      }
    } catch (error) {
      console.error("Error fetching teams:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamRoster = async (teamId, selectedTimeframe, teamsData = null) => {
    // Use passed teamsData or fall back to state
    const teamMap = teamsData || teams;
    const teamName = teamMap[teamId];

    if (!teamName) {
      console.error(`Team name not found for ID: ${teamId}`);
      return;
    }

    // Check if already loaded
    if (rosters[teamName]) {
      return;
    }

    setLoadingRoster(true);
    try {
      const response = await fetch(`/get_roster/${league_id}/${selectedTimeframe}/${teamId}`);
      if (!response.ok) throw new Error('Failed to fetch roster');
      const data = await response.json();

      setRosters(prev => ({
        ...prev,
        [teamName]: data
      }));
    } catch (error) {
      console.error("Error fetching roster:", error);
    } finally {
      setLoadingRoster(false);
    }
  };

  const handleTimeframeChange = (option) => {
    setTimeframe(option);
    // Clear rosters and fetch selected team with new timeframe
    setRosters({});
    if (selectedTeam) {
      fetchTeamRoster(selectedTeam.value, option.value);
    }
  };

  const handleTeamChange = (option) => {
    setSelectedTeam(option);
    fetchTeamRoster(option.value, timeframe.value);
  };

  const handleCopyScript = async () => {
    try {
      const response = await fetch('/bbm_autofill.user.js');
      const scriptContent = await response.text();
      await navigator.clipboard.writeText(scriptContent);
      setCopyStatus('Copied!');
      setTimeout(() => setCopyStatus(''), 2000);
    } catch (error) {
      console.error('Error copying script:', error);
      setCopyStatus('Failed to copy');
      setTimeout(() => setCopyStatus(''), 2000);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  // Get the roster for the selected team
  const selectedTeamName = teams[selectedTeam?.value];
  const displayedRosters = selectedTeamName && rosters[selectedTeamName]
    ? { [selectedTeamName]: rosters[selectedTeamName] }
    : {};

  return (
    <div id="league-overview">
      <div className="pure-g center">
        <div className="pure-u-1-1">
          <header>
            <h1>Roster Overview</h1>
          </header>
        </div>
      </div>

      <div className="pure-g center">
        <div className="pure-u-1-1" style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div
            onClick={() => setShowAutofillInstructions(!showAutofillInstructions)}
            style={{
              background: '#1a1a2e',
              border: '1px solid #4a90e2',
              borderRadius: '6px',
              padding: '12px 15px',
              marginBottom: '15px',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ color: '#4a90e2' }}>BBM Auto-Fill Instructions</strong>
              <span style={{ color: '#4a90e2' }}>{showAutofillInstructions ? '▼' : '▶'}</span>
            </div>
          </div>

          {showAutofillInstructions && (
            <div style={{
              background: '#16213e',
              border: '1px solid #2a3f5f',
              borderRadius: '6px',
              padding: '20px',
              marginBottom: '15px',
              marginTop: '-10px'
            }}>
              <div style={{ marginBottom: '15px' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyScript();
                  }}
                  style={{
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {copyStatus || 'Copy Script'}
                </button>
              </div>

              <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#e0e0e0' }}>
                <p><strong>Step 1:</strong> Click "Copy Script" above</p>
                <p><strong>Step 2:</strong> Navigate to <a href="https://basketballmonster.com/teamanalysis.aspx" target="_blank" rel="noopener noreferrer" style={{ color: '#4a90e2' }}>Team Analysis</a> and log in</p>
                <p><strong>Step 3:</strong> Press <code style={{ background: '#0d1117', padding: '2px 6px', borderRadius: '3px', color: '#e0e0e0' }}>Cmd + Option + J</code> to open the console, then paste and press Enter</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="pure-g center">
          <p className="description-text">
            Compare stats for different teams in one place. Z-scores,
            7/15/30 day stats, comparison tool, etc
          </p>
        </div>
        <br />
        <div className="controls" style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <Select
            options={teamOptions}
            onChange={handleTeamChange}
            value={selectedTeam}
            className="team-select"
            placeholder="Select Team..."
            styles={{ container: (base) => ({ ...base, minWidth: '200px' }) }}
          />
          <Select
            options={timeframeOptions}
            onChange={handleTimeframeChange}
            value={timeframe}
            className="timeframe-select"
            styles={{ container: (base) => ({ ...base, minWidth: '150px' }) }}
          />
        </div>
        <div className="pure-g">
          {loadingRoster ? (
            <div style={{ textAlign: 'center', padding: '20px', width: '100%' }}>
              Loading roster...
            </div>
          ) : (
            Object.entries(displayedRosters).map(([teamName, players]) => (
              <div key={teamName} className="pure-u-1-1">
                <PlayerStatsTable
                  players={players}
                  teamName={teamName}
                  showTeamTotals={true}
                />
                <br />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default RosterDisplay;